import { spansOverlap } from './storyTopicSpans';

import type { TimeSpan } from './storyTopicSpans';
import type { DatoSpeaker, OtherVideo } from '@src/types.ts/story';

/**
 * PLAT-11096 — what narrows the clip picker's list.
 *
 * Three narrowings, and they compose as **AND**: a speaker *and* a phrase means
 * "that person saying that thing". Within one narrowing it is **OR** — two
 * speakers means either of them — because a multi-select whose own values ANDed
 * would return nothing for almost every pair (DS1).
 *
 * Pure, in the shape of `reelEditorSearch.ts`, so the rule is provable without
 * rendering a modal. Everything read here is metadata the picker already holds:
 * no clip is loaded, no transcript is fetched (DS3).
 */

export type ClipPickerFilters = {
  /**
   * Normalized with `normalizeReelQuery` — one definition of "whitespace is not
   * a query" for both search boxes in this epic. Empty means no text narrowing.
   */
  query: string;
  /** Speaker labels, OR-ed. Empty means no speaker narrowing. */
  speakerLabels: readonly string[];
  /** Topic titles, OR-ed. Empty means no topic narrowing. */
  topics: readonly string[];
  /**
   * Where those topics are spoken in the source (`spansForTopics`), which is
   * what a clip is actually matched against — a clip records the *prompt* it was
   * produced from, never a topic (see `storyTopicSpans.ts`). Empty alongside a
   * non-empty `topics` means the chosen topics cover nothing, and every clip is
   * excluded.
   */
  topicSpans: readonly TimeSpan[];
};

export const NO_CLIP_PICKER_FILTERS: ClipPickerFilters = {
  query: '',
  speakerLabels: [],
  topics: [],
  topicSpans: [],
};

/** Whether anything is narrowing the list — what the empty state branches on. */
export const hasActiveClipFilters = (filters: ClipPickerFilters): boolean =>
  !!filters.query ||
  filters.speakerLabels.length > 0 ||
  filters.topics.length > 0;

/**
 * Every speaker label the clip records: its own, plus one per passage for a clip
 * that is itself a reel.
 *
 * **This is the clip's _predominant_ speaker, not everyone heard in it** — clip
 * production records the label that owns 60% or more of the clip's words, and
 * nothing else. So a clip where someone speaks briefly does not carry them, and
 * narrowing by that person will not surface it. Reading the actual speakers would
 * mean loading every candidate clip's transcript to answer a filter, which is
 * exactly what the picker is metadata-only to avoid (DS3).
 *
 * A reel clip matches if **any** of its passages does: it is one row, and hiding
 * it because only one of its three passages is the chosen speaker's would hide a
 * clip that does contain them.
 */
export function clipSpeakerLabels(clip: OtherVideo): string[] {
  const clipJson = clip.clipJson;
  if (!clipJson) return [];

  const labels = [
    clipJson.speakerLabel,
    ...(clipJson.subClips ?? []).map(subClip => subClip.speakerLabel),
  ];
  return [...new Set(labels.filter((label): label is string => !!label))];
}

/**
 * The people a clip records, resolved to speakers of the story and deduplicated
 * by person: two labels can be one speaker once speakers have been combined,
 * and a reel contributes one label per passage. A label with nobody behind it
 * drops out, which is what lets a face pile be absent rather than anonymous.
 *
 * Shared by the picker's rows and the Video Clips cards so a clip shows the same
 * faces in both — and so the {@link clipSpeakerLabels} caveat above (it is the
 * *predominant* speaker, not everyone heard) has one place to be read.
 */
export function clipIdentifiedSpeakers(
  clip: OtherVideo,
  speakersByLabel: ReadonlyMap<string, DatoSpeaker> | undefined,
): DatoSpeaker[] {
  if (!speakersByLabel?.size) return [];

  const byId = new Map<string, DatoSpeaker>();
  for (const label of clipSpeakerLabels(clip)) {
    const speaker = speakersByLabel.get(label);
    if (speaker) byId.set(speaker.id, speaker);
  }
  return [...byId.values()];
}

/**
 * The source the clip plays: one span, or one per passage for a clip that is
 * itself a reel. What the topic filter tests, since the topic's own extent is
 * a stretch of the story (`storyTopicSpans.ts`).
 */
export function clipSourceSpans(clip: OtherVideo): TimeSpan[] {
  const clipJson = clip.clipJson;
  if (!clipJson) return [];

  const ranges: { startTime: number; duration: number }[] =
    clipJson.isReel && clipJson.subClips?.length
      ? clipJson.subClips
      : [clipJson];

  return ranges
    .filter(
      range =>
        typeof range.startTime === 'number' &&
        typeof range.duration === 'number' &&
        range.duration > 0,
    )
    .map(range => ({
      start: range.startTime,
      end: range.startTime + range.duration,
    }));
}

/**
 * The clip's transcript as a row reads it: one line, no doubled whitespace.
 *
 * Every read of the transcript goes through this — matching as well as the
 * snippets below. A clip transcript carries the source's own line breaks, so a
 * phrase the creator remembers as one line ("the whole point is") can be stored
 * with a newline inside it: matched on the raw text that clip is missed, while
 * the snippet (which does flatten) would have found and shown the phrase. The
 * list and what the row displays have to agree about what the clip says.
 */
const flatTranscript = (clip: OtherVideo): string =>
  (clip.transcriptionText ?? '').replace(/\s+/g, ' ').trim();

/**
 * What the text query matches: the heading the row shows, **or** anything said
 * in the clip (DS2). One field rather than two, because what a creator remembers
 * is usually the line rather than which of the two holds it — and it is the same
 * rule `reelRowMatches` applies to a Section.
 */
function matchesQuery(clip: OtherVideo, query: string): boolean {
  if (!query) return true;
  return (
    (clip.title ?? '').toLowerCase().includes(query) ||
    flatTranscript(clip).toLowerCase().includes(query)
  );
}

const intersects = (
  values: readonly string[],
  selected: readonly string[],
): boolean => {
  if (selected.length === 0) return true;
  const wanted = new Set(selected);
  return values.some(value => wanted.has(value));
};

/**
 * Whether the clip plays any of the source the chosen topics cover. A clip with
 * no usable range is excluded rather than matching everything, the same way a
 * clip with no speaker is: "we cannot tell what this is about" is not an answer
 * to "show me the clips about this".
 */
function matchesTopics(clip: OtherVideo, filters: ClipPickerFilters): boolean {
  if (filters.topics.length === 0) return true;
  if (filters.topicSpans.length === 0) return false;

  return clipSourceSpans(clip).some(clipSpan =>
    filters.topicSpans.some(topicSpan => spansOverlap(clipSpan, topicSpan)),
  );
}

/**
 * Whether a clip survives every active narrowing. A clip that records no speaker
 * is excluded by a speaker filter rather than matching everything — the creator
 * asked for that person's clips, and "we do not know whose this is" is not an
 * answer to that. Same for topics.
 */
export function clipMatchesFilters(
  clip: OtherVideo,
  filters: ClipPickerFilters,
): boolean {
  return (
    matchesQuery(clip, filters.query) &&
    intersects(clipSpeakerLabels(clip), filters.speakerLabels) &&
    matchesTopics(clip, filters)
  );
}

/**
 * How much of a clip's transcript a row carries when nothing is searched for.
 * Well past what the collapsed two lines show: the row expands on hover, and
 * this is what it opens into.
 */
const OPENING_CHARS = 700;

/** Context before a match on the row — short, so the phrase is in the first line. */
const SNIPPET_RADIUS = 40;

/** …and after it, which is what the row opens into on hover. */
export const SNIPPET_AFTER_RADIUS = 600;

/** …and in the hover, as a last resort when a paragraph cannot be bounded. */
export const HOVER_SNIPPET_RADIUS = 180;

/** Longest paragraph the hover will show whole before falling back to a window. */
const HOVER_MAX_CHARS = 700;

/**
 * Widens an index back to the start of the word it lands in, so a snippet never
 * opens mid-syllable — "…ferences" reads as a typo rather than as a cut.
 */
function wordStart(text: string, index: number): number {
  let at = Math.max(0, index);
  while (at > 0 && !/\s/.test(text[at - 1])) at -= 1;
  return at;
}

/** The same, forward, to the end of the word an index lands in. */
function wordEnd(text: string, index: number): number {
  let at = Math.min(text.length, index);
  while (at < text.length && !/\s/.test(text[at])) at += 1;
  return at;
}

/**
 * The clip's opening words — the second line a row shows when nothing is being
 * searched for, which is what a Section row does with `firstLine`. A title says
 * what the clip is *called*; this says what it *says*.
 */
export function clipOpeningText(
  clip: OtherVideo,
  maxChars: number = OPENING_CHARS,
): string {
  const transcript = flatTranscript(clip);
  if (!transcript) return '';
  if (transcript.length <= maxChars) return transcript;

  const end = wordEnd(transcript, maxChars);
  const text = transcript.slice(0, end).trim();
  return end < transcript.length ? `${text}…` : text;
}

/** A found phrase, and the transcript either side of it. */
export type ClipTextMatch = {
  before: string;
  match: string;
  after: string;
};

/**
 * Where the query is said in the clip, with context around it — so a search
 * answers "here is the line you were thinking of" rather than only "this clip".
 *
 * `null` when the query is empty, the clip has no transcript, or the query is in
 * the *title* only: there is nothing to point at inside the transcript then, and
 * the row falls back to its opening words.
 *
 * The match is returned split rather than pre-marked up, so the caller decides
 * how to emphasise it and this stays free of anything renderable.
 */
export function clipMatchSnippet(
  clip: OtherVideo,
  query: string,
  radius: number = SNIPPET_RADIUS,
  /**
   * How far to run *past* the match, when that should differ from the run up to
   * it. The row's snippet expands downward on hover, and the two ends serve
   * different jobs there: the leading context stays short so the phrase is in
   * the first line while the row is collapsed, and the trailing context runs
   * long so there is something to read once it opens.
   */
  afterRadius: number = radius,
): ClipTextMatch | null {
  if (!query) return null;

  const transcript = flatTranscript(clip);
  if (!transcript) return null;

  const at = transcript.toLowerCase().indexOf(query);
  if (at === -1) return null;

  const matchEnd = at + query.length;

  // Only the *outer* edges move to a word boundary. The inner ones are the match
  // itself: walking those would pull the phrase into the context either side of
  // it and lose it from the middle.
  const from = wordStart(transcript, at - radius);
  const to = wordEnd(transcript, matchEnd + afterRadius);

  const leading = transcript.slice(from, at).trim();
  const trailing = transcript.slice(matchEnd, to).trim();

  // The spacing is rebuilt around the match rather than sliced with it, so the
  // three parts join back into one readable line whatever was trimmed.
  return {
    before: `${from > 0 ? '…' : ''}${leading}${leading ? ' ' : ''}`,
    match: transcript.slice(at, matchEnd),
    after: `${trailing ? ' ' : ''}${trailing}${to < transcript.length ? '…' : ''}`,
  };
}

/**
 * The **paragraph** the query was found in — what the hover shows, so the row's
 * one-line snippet can be read in the context it came from.
 *
 * Paragraph first, because that is what a creator means by "where does this
 * appear": the transcript's own line breaks. Failing that — a clip transcript is
 * often stored as one unbroken run — the **sentence** around the match, which is
 * the smallest unit that still reads as prose. And failing *that* (a paragraph
 * long enough to be a wall of text in a tooltip), the plain character window,
 * because an unreadably long tooltip is worse than a clipped one.
 *
 * Whitespace is collapsed only at the end, so the boundaries can be found on the
 * text as it is actually stored.
 */
export function clipMatchParagraph(
  clip: OtherVideo,
  query: string,
  maxChars: number = HOVER_MAX_CHARS,
): string | null {
  if (!query) return null;

  const raw = clip.transcriptionText ?? '';
  if (!raw) return null;

  const at = raw.toLowerCase().indexOf(query);
  if (at === -1) return null;
  const matchEnd = at + query.length;

  // The paragraph: between the line breaks either side of the match.
  const breakBefore = raw.lastIndexOf('\n', at);
  const breakAfter = raw.indexOf('\n', matchEnd);
  let from = breakBefore === -1 ? 0 : breakBefore + 1;
  let to = breakAfter === -1 ? raw.length : breakAfter;

  // No line breaks around it, or a paragraph too long to read in a tooltip:
  // narrow to the sentence the match sits in.
  if (to - from > maxChars) {
    const sentenceStart = Math.max(
      ...['.', '!', '?'].map(mark => raw.lastIndexOf(mark, at)),
    );
    const sentenceEnd = Math.min(
      ...['.', '!', '?']
        .map(mark => raw.indexOf(mark, matchEnd))
        .filter(index => index !== -1)
        .concat(raw.length),
    );
    from = sentenceStart === -1 ? from : sentenceStart + 1;
    to = Math.min(to, sentenceEnd === raw.length ? to : sentenceEnd + 1);
  }

  const paragraph = raw.slice(from, to).replace(/\s+/g, ' ').trim();
  if (!paragraph) return null;

  // Still too long — the "sentence" was itself a run without punctuation. Fall
  // back to the window, which is bounded by construction.
  if (paragraph.length > maxChars) {
    const window = clipMatchSnippet(clip, query, HOVER_SNIPPET_RADIUS);
    return window
      ? `${window.before}${window.match}${window.after}`
      : paragraph.slice(0, maxChars);
  }

  return `${from > 0 ? '…' : ''}${paragraph}${to < raw.length ? '…' : ''}`;
}

/** A run of text, and whether it is the searched phrase. */
export type HighlightSegment = { text: string; isMatch: boolean };

/**
 * Splits text into the searched phrase and everything between, so a caller can
 * emphasise the matches without building markup here.
 *
 * **Every** occurrence, not only the first: the row's snippet can hold two, and
 * the paragraph behind the hover often holds several — highlighting one of five
 * would read as the wrong one being picked out.
 *
 * `query` is expected normalized (lower-cased); the text is lowered here for the
 * scan and sliced from the original, so the segments keep the transcript's own
 * casing.
 */
export function highlightSegments(
  text: string,
  query: string,
): HighlightSegment[] {
  if (!query || !text) return [{ text, isMatch: false }];

  const haystack = text.toLowerCase();
  const segments: HighlightSegment[] = [];
  let from = 0;

  for (;;) {
    const at = haystack.indexOf(query, from);
    if (at === -1) break;
    if (at > from) {
      segments.push({ text: text.slice(from, at), isMatch: false });
    }
    segments.push({ text: text.slice(at, at + query.length), isMatch: true });
    from = at + query.length;
  }

  if (from < text.length) {
    segments.push({ text: text.slice(from), isMatch: false });
  }
  return segments.length > 0 ? segments : [{ text, isMatch: false }];
}
